import React, { useState, useEffect, useRef } from 'react';

const FILE_TYPE_STYLES = {
  IEP: 'bg-indigo-100 text-indigo-700 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:border-indigo-800',
  evaluation: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800',
  progress_report: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800',
  accommodation: 'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-800',
  other: 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-600',
};

const FILE_TYPE_LABELS = {
  IEP: 'IEP',
  evaluation: 'Evaluation',
  progress_report: 'Progress Report',
  accommodation: 'Accommodation',
  other: 'Other',
};

function formatFileSize(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return '—'; }
}

function FileIcon({ mimeType }) {
  const cls = 'w-5 h-5';
  if (mimeType?.startsWith('image/')) {
    return <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z" /></svg>;
  }
  if (mimeType === 'application/pdf') {
    return <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>;
  }
  return <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>;
}

function Checkbox({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={(e) => { e.stopPropagation(); onChange(!checked); }}
      className={`w-4.5 h-4.5 w-[18px] h-[18px] rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
        checked
          ? 'bg-indigo-600 border-indigo-600 text-white'
          : 'border-gray-300 dark:border-slate-600 hover:border-indigo-400 dark:hover:border-indigo-500'
      }`}
    >
      {checked && <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
    </button>
  );
}

export default function ListFileCabinet({ files, loading, onView, onEdit, onDelete, onDownload, selectedIds = [], onSelectionChange }) {
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuAbove, setMenuAbove] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const menuRefs = useRef({});

  const handleDownloadClick = async (file) => {
    setOpenMenuId(null);
    if (!onDownload || downloadingId) return;
    setDownloadingId(file.id);
    try {
      await onDownload(file);
    } finally {
      setDownloadingId(null);
    }
  };

  const allSelected = files.length > 0 && selectedIds.length === files.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < files.length;

  const toggleOne = (id) => {
    if (selectedIds.includes(id)) onSelectionChange(selectedIds.filter(x => x !== id));
    else onSelectionChange([...selectedIds, id]);
  };

  const toggleAll = () => {
    if (allSelected) onSelectionChange([]);
    else onSelectionChange(files.map(f => f.id));
  };

  const toggleMenu = (id) => {
    if (openMenuId === id) { setOpenMenuId(null); return; }
    const ref = menuRefs.current[id];
    if (ref) {
      const rect = ref.getBoundingClientRect();
      setMenuAbove(window.innerHeight - rect.bottom < 220);
    }
    setOpenMenuId(id);
  };

  useEffect(() => {
    function handleClickOutside(e) {
      if (openMenuId !== null) {
        const ref = menuRefs.current[openMenuId];
        if (ref && !ref.contains(e.target)) setOpenMenuId(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuId]);

  if (files.length === 0) {
    return (
      <div className="px-6 py-16 text-center">
        <div className="w-14 h-14 rounded-2xl bg-gray-100 dark:bg-slate-700 flex items-center justify-center mx-auto mb-4">
          <svg className="w-7 h-7 text-gray-400 dark:text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
          </svg>
        </div>
        <p className="text-gray-900 dark:text-white font-semibold">No files found</p>
        <p className="text-gray-500 dark:text-slate-400 text-sm mt-1">Upload documents to get started</p>
      </div>
    );
  }

  return (
    <>
      {/* Desktop table */}
      <div className="hidden md:block overflow-visible">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-100 dark:border-slate-700 bg-gray-50/60 dark:bg-slate-800/80">
              <th className="pl-5 pr-2 py-3.5 w-10">
                <Checkbox
                  checked={allSelected}
                  onChange={toggleAll}
                />
                {someSelected && <span className="sr-only">Some selected</span>}
              </th>
              <th className="px-3 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">File</th>
              <th className="px-3 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Type</th>
              <th className="px-3 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider hidden lg:table-cell">Size</th>
              <th className="px-3 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Date</th>
              <th className="px-3 py-3.5 text-center text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-slate-700/60">
            {files.map((f) => {
              const isChecked = selectedIds.includes(f.id);
              return (
                <tr key={f.id} className={`transition-colors ${isChecked ? 'bg-indigo-50/50 dark:bg-indigo-900/10' : 'hover:bg-gray-50/60 dark:hover:bg-slate-700/20'}`}>
                  <td className="pl-5 pr-2 py-4">
                    <Checkbox checked={isChecked} onChange={() => toggleOne(f.id)} />
                  </td>
                  <td className="px-3 py-4">
                    <button onClick={() => onView(f)} className="flex items-center gap-3 group text-left">
                      <div className="w-9 h-9 rounded-xl bg-indigo-50 dark:bg-indigo-600/20 flex items-center justify-center flex-shrink-0 text-indigo-600 dark:text-indigo-400">
                        <FileIcon mimeType={f.mime_type} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-gray-900 dark:text-white font-semibold text-sm truncate max-w-[220px] group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                          {f.file_name}
                        </p>
                        {f.description && (
                          <p className="text-gray-400 dark:text-slate-500 text-xs truncate max-w-[220px] mt-0.5">{f.description}</p>
                        )}
                      </div>
                    </button>
                  </td>
                  <td className="px-3 py-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium border ${FILE_TYPE_STYLES[f.file_type] || FILE_TYPE_STYLES.other}`}>
                        {FILE_TYPE_LABELS[f.file_type] || f.file_type}
                      </span>
                      {f.processing_status === 'dob_mismatch' && (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-[#D97706] bg-amber-50 dark:bg-amber-900/20 dark:text-amber-400 border border-amber-200 dark:border-amber-800/40"
                          title="This file was skipped because the DOB does not match the student record."
                        >
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                          DOB Mismatch
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-4 hidden lg:table-cell">
                    <span className="text-gray-600 dark:text-slate-300 text-sm">{formatFileSize(f.file_size)}</span>
                  </td>
                  <td className="px-3 py-4">
                    <span className="text-gray-600 dark:text-slate-300 text-sm">{formatDate(f.created_at)}</span>
                  </td>
                  <td className="px-3 py-4 text-center">
                    <div className="relative flex justify-center" ref={(el) => { menuRefs.current[f.id] = el; }}>
                      <button
                        onClick={() => toggleMenu(f.id)}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                        aria-label="Actions"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <circle cx="12" cy="5" r="1.5" />
                          <circle cx="12" cy="12" r="1.5" />
                          <circle cx="12" cy="19" r="1.5" />
                        </svg>
                      </button>
                      {openMenuId === f.id && (
                        <div className={`absolute right-0 ${menuAbove ? 'bottom-full mb-1.5' : 'top-full mt-1.5'} w-44 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl shadow-xl z-50 py-1`}>
                          <button onClick={() => { setOpenMenuId(null); onView(f); }} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                            <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                            Preview
                          </button>
                          <button onClick={() => { setOpenMenuId(null); onEdit(f); }} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                            <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                            Edit Details
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDownloadClick(f)}
                            disabled={downloadingId === f.id}
                            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors disabled:opacity-50"
                          >
                            <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                            {downloadingId === f.id ? 'Downloading...' : 'Download'}
                          </button>
                          <div className="my-1 border-t border-gray-100 dark:border-slate-700" />
                          <button onClick={() => { setOpenMenuId(null); onDelete(f); }} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile card layout */}
      <div className="md:hidden divide-y divide-gray-100 dark:divide-slate-700/60">
        {files.map((f) => {
          const isChecked = selectedIds.includes(f.id);
          return (
            <div key={f.id} className={`p-4 flex items-start gap-3 ${isChecked ? 'bg-indigo-50/50 dark:bg-indigo-900/10' : ''}`}>
              <div className="pt-1 flex-shrink-0">
                <Checkbox checked={isChecked} onChange={() => toggleOne(f.id)} />
              </div>
              <button onClick={() => onView(f)} className="w-10 h-10 rounded-xl bg-indigo-50 dark:bg-indigo-600/20 flex items-center justify-center flex-shrink-0 text-indigo-600 dark:text-indigo-400">
                <FileIcon mimeType={f.mime_type} />
              </button>
              <div className="flex-1 min-w-0">
                <button onClick={() => onView(f)} className="text-left w-full">
                  <p className="text-gray-900 dark:text-white font-semibold text-sm truncate">{f.file_name}</p>
                </button>
                <div className="flex flex-wrap items-center gap-2 mt-1.5">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium border ${FILE_TYPE_STYLES[f.file_type] || FILE_TYPE_STYLES.other}`}>
                    {FILE_TYPE_LABELS[f.file_type] || f.file_type}
                  </span>
                  {f.processing_status === 'dob_mismatch' && (
                    <span
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium text-[#D97706] bg-amber-50 dark:bg-amber-900/20 dark:text-amber-400"
                      title="This file was skipped because the DOB does not match the student record."
                    >
                      ⚠ DOB Mismatch
                    </span>
                  )}
                  <span className="text-gray-400 dark:text-slate-500 text-xs">{formatFileSize(f.file_size)}</span>
                  <span className="text-gray-400 dark:text-slate-500 text-xs">{formatDate(f.created_at)}</span>
                </div>
              </div>
              <div className="relative flex-shrink-0" ref={(el) => { menuRefs.current[`m-${f.id}`] = el; }}>
                <button
                  onClick={() => toggleMenu(`m-${f.id}`)}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <circle cx="12" cy="5" r="1.5" />
                    <circle cx="12" cy="12" r="1.5" />
                    <circle cx="12" cy="19" r="1.5" />
                  </svg>
                </button>
                {openMenuId === `m-${f.id}` && (
                  <div className={`absolute right-0 ${menuAbove ? 'bottom-full mb-1.5' : 'top-full mt-1.5'} w-44 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl shadow-xl z-50 py-1`}>
                    <button onClick={() => { setOpenMenuId(null); onView(f); }} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                      <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                      Preview
                    </button>
                    <button onClick={() => { setOpenMenuId(null); onEdit(f); }} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                      <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                      Edit Details
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDownloadClick(f)}
                      disabled={downloadingId === f.id}
                      className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors disabled:opacity-50"
                    >
                      <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                      {downloadingId === f.id ? 'Downloading...' : 'Download'}
                    </button>
                    <div className="my-1 border-t border-gray-100 dark:border-slate-700" />
                    <button onClick={() => { setOpenMenuId(null); onDelete(f); }} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
